/**
 * Which user bubble should echo the armed / launched command chip.
 *
 * The composer already shows `ActiveWorkflowChip` while a command is armed or
 * a run is live. The same label belongs on the originating user message so the
 * sent turn keeps the command context after it leaves the box — the same
 * placement as source chips above a question.
 */

import { isWorkflowRunAskTurnAskId } from './workflowRunAskTurnAskId'

export interface CommandChipTurn {
  id: string
  role: string
  createdAt?: string
  askId?: string | null
  commandDisplayName?: string
  /**
   * The audience the command was bound to, stamped at send beside
   * `commandDisplayName` (plan 2026-09-14-twin-test-bound-audience, B4). Two
   * chips, never one renamed chip: this NEVER replaces the command name, it sits
   * beside it. Absent when nothing could name the bound audience, and the turn
   * then shows the command chip alone.
   */
  commandAudienceName?: string
}

export interface UserTurnCommandChipOptions {
  lastQuestionId: string | null
  originatingTurnId: string | null
  armedDisplayName: string | null
  runDisplayName: string | null
}

export interface UserTurnAudienceChipOptions {
  lastQuestionId: string | null
  originatingTurnId: string | null
  armedAudienceName: string | null
  runAudienceName: string | null
}

/**
 * Last user turn that already existed when the run started. Later follow-ups
 * must not steal the chip. A turn with no `createdAt` is treated as current
 * (optimistic send) and wins if it is the newest user turn.
 */
export function originatingCommandUserTurnId(
  turns: Array<CommandChipTurn>,
  runCreatedAt: string | null,
): string | null {
  if (!runCreatedAt) {
    return null
  }

  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const turn = turns[i]
    if (turn.role !== 'user') {
      continue
    }
    if (!turn.createdAt || turn.createdAt <= runCreatedAt) {
      return turn.id
    }
  }

  return null
}

export function lastUserTurnId(turns: Array<CommandChipTurn>): string | null {
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    if (turns[i].role === 'user') {
      return turns[i].id
    }
  }

  return null
}

interface WorkflowRunStartSource {
  createdAt?: string
  startedAt?: string | null
  steps: Array<{
    startedAt?: string | null
    createdAt?: string
    stepKind: string
  }>
}

/**
 * Earliest reliable clock on the latest thread run: `startedAt`, then
 * `createdAt`, then the first `quick_study` step's timestamps.
 */
export function resolveWorkflowRunStartAt(
  run: WorkflowRunStartSource | null | undefined,
): string | null {
  if (!run) {
    return null
  }

  if (run.startedAt) {
    return run.startedAt
  }

  if (run.createdAt) {
    return run.createdAt
  }

  for (const step of run.steps) {
    if (step.stepKind !== 'quick_study') {
      continue
    }
    if (step.startedAt) {
      return step.startedAt
    }
    if (step.createdAt) {
      return step.createdAt
    }
  }

  return null
}

/**
 * Index of the last turn in the originating ask's exchange that still sits
 * *before* the current-exchange split. Null when the ask *is* the newest
 * question — results stay inside `thread-current-exchange` in that case.
 */
export function historicStudyPinAfterIndex(
  turns: Array<{ id: string; role: string }>,
  originatingAskId: string | null,
  exchangeStart: number,
): number | null {
  if (!originatingAskId) {
    return null
  }

  const askIndex = turns.findIndex((turn) => turn.id === originatingAskId)
  if (askIndex < 0 || askIndex >= exchangeStart) {
    return null
  }

  let end = askIndex
  for (let i = askIndex + 1; i < exchangeStart; i += 1) {
    if (turns[i].role === 'user') {
      break
    }
    end = i
  }

  return end
}

export function userTurnCommandChipLabel(
  turn: CommandChipTurn,
  options: UserTurnCommandChipOptions,
): string | null {
  if (turn.role !== 'user') {
    return null
  }

  if (turn.commandDisplayName) {
    return turn.commandDisplayName
  }

  if (isWorkflowRunAskTurnAskId(turn.askId) && options.runDisplayName) {
    return options.runDisplayName
  }

  if (options.originatingTurnId && turn.id === options.originatingTurnId) {
    return options.runDisplayName
  }

  if (options.armedDisplayName && turn.id === options.lastQuestionId) {
    return options.armedDisplayName
  }

  return null
}

/**
 * The SECOND chip on the same user turn: the audience the command was bound to
 * (plan 2026-09-14-twin-test-bound-audience, B4). Same precedence as
 * `userTurnCommandChipLabel`, one source at a time — the turn's own stamp, then
 * the run this turn started, then the armed command still sitting on the last
 * question. Null when nothing can name it, and the turn shows the command chip
 * alone rather than a blank second chip.
 */
export function userTurnAudienceChipLabel(
  turn: CommandChipTurn,
  options: UserTurnAudienceChipOptions,
): string | null {
  if (turn.role !== 'user') {
    return null
  }

  if (turn.commandAudienceName) {
    return turn.commandAudienceName
  }

  if (isWorkflowRunAskTurnAskId(turn.askId) && options.runAudienceName) {
    return options.runAudienceName
  }

  if (options.originatingTurnId && turn.id === options.originatingTurnId) {
    return options.runAudienceName
  }

  if (options.armedAudienceName && turn.id === options.lastQuestionId) {
    return options.armedAudienceName
  }

  return null
}
